import { AuthError, parseResolvedScope } from '../../chunk-26O3XQW6.mjs';
import { getCurrentUser, fetchUserAttributes, signIn, confirmSignIn, signUp, fetchAuthSession, confirmSignUp, resendSignUpCode, resetPassword, confirmResetPassword, updatePassword, signOut, fetchMFAPreference, setUpTOTP, verifyTOTPSetup, updateMFAPreference } from 'aws-amplify/auth';
import { decodeJwt } from 'jose';

var CONTEXT_MAX_PAGES = 50;
function mapAmplifyError(e) {
  if (e instanceof AuthError) return e;
  if (!(e instanceof Error)) {
    return new AuthError("UNKNOWN", "An unexpected error occurred.");
  }
  switch (e.name) {
    case "NotAuthorizedException":
    case "UserNotFoundException":
      return new AuthError("INVALID_CREDENTIALS", e.message);
    case "UserNotConfirmedException":
      return new AuthError("USER_NOT_CONFIRMED", e.message);
    case "CodeMismatchException":
      return new AuthError("CODE_MISMATCH", e.message);
    case "ExpiredCodeException":
      return new AuthError("EXPIRED_CODE", e.message);
    case "InvalidPasswordException":
      return new AuthError("PASSWORD_POLICY_VIOLATION", e.message);
    case "UsernameExistsException":
      return new AuthError("USER_ALREADY_EXISTS", e.message);
    case "LimitExceededException":
    case "TooManyRequestsException":
    case "TooManyFailedAttemptsException":
      return new AuthError("LIMIT_EXCEEDED", e.message);
    case "NetworkError":
      return new AuthError("NETWORK_ERROR", e.message);
    default:
      return new AuthError("UNKNOWN", e.message);
  }
}
async function tryAmplify(fn) {
  try {
    return await fn();
  } catch (e) {
    throw mapAmplifyError(e);
  }
}
function isLingeringSessionError(e) {
  if (!(e instanceof Error)) return false;
  if (e.name === "UserAlreadyAuthenticatedException") return true;
  return e.name === "NotAuthorizedException" && /revoked/i.test(e.message);
}
function mapCognitoMfaType(type) {
  switch (type) {
    case "TOTP":
      return "TOTP";
    case "SMS":
      return "SMS";
    case "EMAIL":
      return "EMAIL";
    default:
      return null;
  }
}
var CognitoAuthProvider = class {
  /**
   * Deployment-specific configuration injected by the host app — see
   * {@link CognitoAuthProviderConfig}. To swap Cognito for another provider in a
   * fork, implement {@link AuthProviderAdapter} directly instead of constructing
   * this class.
   */
  constructor(config) {
    this.config = config;
  }
  config;
  /**
   * Memoized memberships, used only to resolve a tenantId → its env mode for
   * {@link mintPartnerApiToken}. Stable for a session (a tenant's env mode
   * doesn't change); cleared on signOut so a new identity never reads the
   * previous user's mapping.
   */
  cachedMemberships = null;
  async getCurrentUser() {
    try {
      await getCurrentUser();
      const attrs = await fetchUserAttributes();
      return this.attributesToUser(attrs);
    } catch {
      return null;
    }
  }
  async signIn(input) {
    const credentials = {
      username: input.email.toLowerCase(),
      password: input.password
    };
    try {
      const result = await signIn(credentials);
      return this.mapSignInStep(result.nextStep);
    } catch (e) {
      if (!isLingeringSessionError(e)) throw mapAmplifyError(e);
      await this.signOut().catch(() => void 0);
      const result = await tryAmplify(() => signIn(credentials));
      return this.mapSignInStep(result.nextStep);
    }
  }
  async confirmSignIn(input) {
    const result = await tryAmplify(
      () => confirmSignIn({
        challengeResponse: input.challengeResponse
      })
    );
    return this.mapSignInStep(result.nextStep);
  }
  async signUp(input) {
    const userAttributes = {
      email: input.email.toLowerCase(),
      given_name: input.firstName,
      family_name: input.lastName
    };
    if (input.metadata) {
      for (const [key, value] of Object.entries(input.metadata)) {
        userAttributes[`custom:${key}`] = value;
      }
    }
    const result = await tryAmplify(
      () => signUp({
        username: input.email.toLowerCase(),
        password: input.password,
        options: { userAttributes }
      })
    );
    if (result.nextStep.signUpStep === "DONE") {
      return { kind: "COMPLETE" };
    }
    return { kind: "CONFIRMATION_REQUIRED", method: "CODE" };
  }
  async confirmSignUp(input) {
    await tryAmplify(
      () => confirmSignUp({
        username: input.email.toLowerCase(),
        confirmationCode: input.code
      })
    );
  }
  async resendSignUpCode(input) {
    await tryAmplify(
      () => resendSignUpCode({
        username: input.email.toLowerCase()
      })
    );
  }
  async forgotPassword(input) {
    await tryAmplify(() => resetPassword({ username: input.email.toLowerCase() }));
  }
  async confirmForgotPassword(input) {
    await tryAmplify(
      () => confirmResetPassword({
        username: input.email.toLowerCase(),
        confirmationCode: input.code,
        newPassword: input.newPassword
      })
    );
  }
  async changePassword(input) {
    await tryAmplify(
      () => updatePassword({
        oldPassword: input.oldPassword,
        newPassword: input.newPassword
      })
    );
  }
  async signOut() {
    this.cachedMemberships = null;
    await tryAmplify(() => signOut({ global: true }));
  }
  async getIdToken() {
    const { tokens } = await tryAmplify(() => fetchAuthSession());
    return tokens?.idToken?.toString() ?? null;
  }
  // ------------------------------------------------------------------------
  // Multi-tenancy. The Cognito reference implementation:
  //   - active tenant lives in the `active_tenant` JWT claim (Pre-Token
  //     Generation Lambda);
  //   - switching POSTs the developer API + force-refreshes the session so
  //     the next request carries the updated claim;
  //   - memberships come from the developer-API memberships endpoint.
  // ------------------------------------------------------------------------
  async getMemberships() {
    const idToken = await this.getIdToken();
    if (!idToken) return [];
    const resp = await fetch(`${this.config.developerApiBase}/developer/memberships`, {
      headers: { Authorization: `Bearer ${idToken}` }
    });
    if (!resp.ok) {
      if (resp.status === 401 || resp.status === 403 || resp.status === 404) return [];
      const body = await resp.text().catch(() => "");
      throw new AuthError("UNKNOWN", `Failed to load memberships: ${resp.status} ${body}`);
    }
    const json = await resp.json().catch(() => null);
    let rows = [];
    if (Array.isArray(json)) {
      rows = json;
    } else if (json && typeof json === "object") {
      const envelope = json.memberships;
      if (Array.isArray(envelope)) rows = envelope;
    }
    return rows.map((r) => this.mapMembership(r));
  }
  async getActiveTenant() {
    const idToken = await this.getIdToken();
    if (!idToken) return null;
    try {
      const claims = decodeJwt(idToken);
      const tenant = claims["active_tenant"];
      return typeof tenant === "string" && tenant.length > 0 ? tenant : null;
    } catch {
      return null;
    }
  }
  async getActivePartnerUserId() {
    const idToken = await this.getIdToken();
    if (!idToken) return null;
    try {
      const claims = decodeJwt(idToken);
      const id = claims["active_partner_user_id"];
      return typeof id === "string" && id.length > 0 ? id : null;
    } catch {
      return null;
    }
  }
  async setActiveTenant(tenantId) {
    const idToken = await this.getIdToken();
    if (!idToken) {
      throw new AuthError("INVALID_CREDENTIALS", "Not authenticated \u2014 cannot switch tenant.");
    }
    const resp = await fetch(`${this.config.developerApiBase}/developer/active-tenant`, {
      method: "POST",
      headers: { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" },
      body: JSON.stringify({ tenantId })
    });
    if (!resp.ok) {
      const body = await resp.text().catch(() => "");
      throw new AuthError("UNKNOWN", `Failed to switch active tenant: ${resp.status} ${body}`);
    }
    const result = await resp.json().catch(() => ({}));
    if (result.requiresJwtRefresh) {
      await tryAmplify(() => fetchAuthSession({ forceRefresh: true }));
    }
  }
  async checkUserExists(email) {
    const target = email.toLowerCase();
    const current = await this.getCurrentUser();
    if (current) {
      const isMe = current.email.toLowerCase() === target;
      return { exists: isMe, isMe };
    }
    return { exists: false, isMe: false };
  }
  async linkInvitation(inviteToken) {
    const idToken = await this.getIdToken();
    if (!idToken) {
      throw new AuthError(
        "INVALID_CREDENTIALS",
        "Not authenticated \u2014 sign in before linking an invitation."
      );
    }
    const resp = await fetch(`${this.config.developerApiBase}/developer/link-invitation`, {
      method: "POST",
      headers: { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" },
      // Backend body field is snake_case `invite_token`.
      body: JSON.stringify({ invite_token: inviteToken })
    });
    if (!resp.ok) {
      const body = await resp.text().catch(() => "");
      throw new AuthError("UNKNOWN", `Failed to link invitation: ${resp.status} ${body}`);
    }
    const data = await resp.json();
    return {
      tenantId: data.tenantId,
      partnerUserId: data.partnerUserId,
      role: data.role ?? "SUB_USER",
      alreadyActive: data.alreadyActive ?? false
    };
  }
  /**
   * Mint a short-lived partner-API bearer (st_*) scoped to `tenantId`. The
   * Admin App authenticates to the partner API (/v1/*) with THIS, not the
   * Cognito id token. Returns the token + its expiry (epoch ms).
   *
   * NOT part of AuthProviderAdapter: this is the Vectros-specific partner-API
   * bridge (it mints via the developer API's scoped-token endpoint, whose
   * `tenant=live|test` param is derived from the tenant's membership kind).
   * The host app wires it into the token cache via setPartnerApiTokenMinter; a
   * partner fork swaps the provider + wires its own minter. Keeping the
   * /developer/* call HERE (not in the shared cache) keeps the reference apps
   * forkable.
   *
   * `contextId` (optional): when supplied, the mint requests a token whose
   * `context_id` claim is that AppContext (the data-plane context switcher —
   * app.vectros.ai). The server validates the caller's access to it and 404s
   * uniformly otherwise (the per-context mint). Omitted →
   * tenant-default context (admin-app's control-plane behavior). Until the
   * backend per-context mint ships, the param is accepted by the endpoint
   * but resolves to the default context.
   */
  async mintPartnerApiToken(tenantId, contextId) {
    const idToken = await this.getIdToken();
    if (!idToken) {
      throw new AuthError("INVALID_CREDENTIALS", "Not authenticated \u2014 cannot mint a Vectros API token.");
    }
    const kind = await this.resolveTenantKind(tenantId);
    const contextParam = contextId ? `&context=${encodeURIComponent(contextId)}` : "";
    const url = `${this.config.developerApiBase}/developer/scoped-token?tenant=${encodeURIComponent(kind)}&ttl=900${contextParam}`;
    const resp = await fetch(url, {
      method: "GET",
      headers: { Authorization: `Bearer ${idToken}` }
    });
    if (!resp.ok) {
      const body = await resp.text().catch(() => "");
      throw new AuthError("UNKNOWN", `Vectros API token mint failed: ${resp.status} ${body}`);
    }
    const { token, expiresAt, resolvedScope } = await resp.json();
    return {
      token,
      expiresAtMs: expiresAt * 1e3,
      // backend returns epoch seconds
      resolvedScope: parseResolvedScope(resolvedScope)
    };
  }
  /**
   * List the app contexts the OWNER can reach in `tenantId`. Reads the
   * OWNER-gated developer-API enumeration (`GET /developer/app-contexts?tenant=
   * <live|test>`) — NOT the partner data API, whose `listAppContexts` confines a
   * context-scoped token to its own context (so it can't enumerate siblings).
   * The `tenant` kind is derived from the membership, mirroring the scoped-token
   * mint. Paginates fully; returns [] on no-session / not-an-owner (403/404).
   *
   * `options.onlyMine` adds `mine=true`, which narrows the enumeration to the
   * contexts the caller holds an active access profile in (their provisioned
   * contexts) instead of the full tenant set — the data-plane switcher's view.
   */
  async listAppContexts(tenantId, options) {
    const idToken = await this.getIdToken();
    if (!idToken) return [];
    const kind = await this.resolveTenantKind(tenantId);
    const out = [];
    let startFrom;
    for (let page = 0; page <= CONTEXT_MAX_PAGES; page++) {
      const qs = new URLSearchParams({ tenant: kind, limit: "100" });
      if (options?.onlyMine) qs.set("mine", "true");
      if (startFrom) qs.set("startFrom", startFrom);
      const resp = await fetch(
        `${this.config.developerApiBase}/developer/app-contexts?${qs.toString()}`,
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      if (!resp.ok) {
        if (resp.status === 401 || resp.status === 403 || resp.status === 404) {
          if (startFrom === void 0) return out;
          throw new AuthError(
            "UNKNOWN",
            `Failed to list app contexts: ${resp.status} part-way through the listing (${out.length} read). Refusing to return a partial result.`
          );
        }
        const body = await resp.text().catch(() => "");
        throw new AuthError("UNKNOWN", `Failed to list app contexts: ${resp.status} ${body}`);
      }
      const json = await resp.json().catch(() => null);
      for (const r of json?.data ?? []) {
        if (r.contextId) {
          out.push({
            contextId: r.contextId,
            name: r.name && r.name.length > 0 ? r.name : r.contextId,
            ...r.status ? { status: r.status } : {}
          });
        }
      }
      const next = json?.nextCursor ?? null;
      if (!next) return out;
      startFrom = next;
    }
    throw new AuthError(
      "UNKNOWN",
      `Failed to list app contexts: still not exhausted after ${CONTEXT_MAX_PAGES + 1} requests (${out.length} read). Refusing to return a partial result.`
    );
  }
  // ------------------------------------------------------------------------
  // Multi-factor auth — TOTP enrollment + management. All Amplify
  // MFA calls live here so the rest of the app sees only the adapter contract.
  // ------------------------------------------------------------------------
  async getMfaStatus() {
    return tryAmplify(async () => {
      const pref = await fetchMFAPreference();
      const enabled = (pref.enabled ?? []).map((m) => mapCognitoMfaType(m)).filter((m) => m !== null);
      const preferred = pref.preferred ? mapCognitoMfaType(pref.preferred) : null;
      return { enabled, preferred };
    });
  }
  async setUpTotp() {
    return tryAmplify(async () => {
      const details = await setUpTOTP();
      const current = await this.getCurrentUser();
      const accountName = current?.email ?? this.config.productName;
      const uri = details.getSetupUri(this.config.productName, accountName);
      return { secret: details.sharedSecret, otpauthUri: uri.toString() };
    });
  }
  async verifyTotpSetup(code) {
    await tryAmplify(async () => {
      await verifyTOTPSetup({ code });
      await updateMFAPreference({ totp: "PREFERRED" });
    });
  }
  async disableTotp() {
    await tryAmplify(() => updateMFAPreference({ totp: "DISABLED" }));
  }
  // ------------------------------------------------------------------------
  // Private mapping helpers — keep all Amplify-specific knowledge isolated.
  // ------------------------------------------------------------------------
  /**
   * Resolve a tenantId to its env mode ('live'|'test') for the scoped-token
   * mint. Reads from the memoized memberships, fetching once if absent. Defaults
   * to 'live' if the id isn't found (safer than 'test' for a tenant the user
   * holds an active membership in; a not-found id is a caller bug anyway).
   */
  async resolveTenantKind(tenantId) {
    if (!this.cachedMemberships) {
      this.cachedMemberships = await this.getMemberships();
    }
    const match = this.cachedMemberships.find((m) => m.tenantId === tenantId);
    return match?.tenantKind === "test" ? "test" : "live";
  }
  /** Map a memberships-endpoint row onto our normalized TenantMembership. */
  mapMembership(r) {
    return {
      tenantId: r.tenantId,
      // Fallbacks are defensive only — the backend supplies all fields. Name
      // falls back to the id; kind/role/status to the safe defaults the
      // backend itself uses for legacy/oddity rows.
      tenantName: r.tenantName ?? r.tenantId,
      tenantKind: r.tenantKind ?? "live",
      role: r.role ?? "SUB_USER",
      status: r.status ?? "ACTIVE",
      partnerId: r.partnerId ?? ""
    };
  }
  attributesToUser(attrs) {
    const email = attrs["email"];
    const sub = attrs["sub"];
    if (!email || !sub) return null;
    return {
      sub,
      email,
      firstName: attrs["given_name"] ?? null,
      lastName: attrs["family_name"] ?? null
    };
  }
  /**
   * Maps Amplify's `signInStep` string onto our SignInResult union. Unknown
   * step values throw `AuthError('UNKNOWN', detailed message)` — silent
   * fall-through on a new Amplify version is a real-world security risk
   * (an unrecognized challenge that we treated as "done" would let a half-
   * authenticated session proceed).
   *
   * Using AuthError (rather than a raw Error) keeps the contract consistent:
   * pages dispatch on `error.code`, and the diagnostic message survives in
   * `error.message` for developer debugging.
   */
  mapSignInStep(nextStep) {
    switch (nextStep.signInStep) {
      case "DONE":
        return { kind: "COMPLETE" };
      case "CONFIRM_SIGN_UP":
        return { kind: "CONFIRMATION_REQUIRED" };
      case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED":
        return { kind: "NEW_PASSWORD_REQUIRED" };
      case "CONFIRM_SIGN_IN_WITH_TOTP_CODE":
        return { kind: "MFA_REQUIRED", methods: ["TOTP"] };
      case "CONFIRM_SIGN_IN_WITH_SMS_CODE":
        return { kind: "MFA_REQUIRED", methods: ["SMS"] };
      case "CONFIRM_SIGN_IN_WITH_EMAIL_CODE":
        return { kind: "MFA_REQUIRED", methods: ["EMAIL"] };
      case "CONTINUE_SIGN_IN_WITH_MFA_SELECTION": {
        const methods = (nextStep.allowedMFATypes ?? []).map((m) => mapCognitoMfaType(m)).filter((m) => m !== null);
        return { kind: "MFA_REQUIRED", methods: methods.length > 0 ? methods : ["TOTP", "SMS"] };
      }
      case "CONTINUE_SIGN_IN_WITH_TOTP_SETUP": {
        const details = nextStep.totpSetupDetails;
        if (details) {
          return {
            kind: "TOTP_SETUP_REQUIRED",
            setup: {
              secret: details.sharedSecret,
              otpauthUri: details.getSetupUri(this.config.productName).toString()
            }
          };
        }
        return { kind: "MFA_REQUIRED", methods: ["TOTP"] };
      }
      default:
        throw new AuthError(
          "UNKNOWN",
          `CognitoAuthProvider: unrecognized sign-in step "${nextStep.signInStep}". This may indicate an Amplify version update introduced a new challenge type; map it explicitly in mapSignInStep().`
        );
    }
  }
};

export { CognitoAuthProvider };
//# sourceMappingURL=cognito.mjs.map
//# sourceMappingURL=cognito.mjs.map